import { Piece, SocialPlacementChannel } from './piece-list';

export type PiecePriceRates = {
  ugc: number;
  advertising: number;
  social: number;
  duration15: number;
  duration30: number;
  duration60: number;
};

export type AudienceFees = Partial<Record<SocialPlacementChannel, number>>;

export type PieceQuote = {
  uniquePieces: number;
  posts: number;
  net: number;
  adLicensesIncluded: number;
  adLicenseOptional: boolean;
};

export function quotePieces(input: {
  pieces: Piece[];
  rates: PiecePriceRates;
  rankMultiplier: number;
  audienceFees: AudienceFees;
  adLicenseSelected: boolean;
}): PieceQuote {
  const rank = input.rankMultiplier > 0 ? input.rankMultiplier : 1;
  let net = 0;
  let posts = 0;
  let adLicensesIncluded = 0;

  for (const piece of input.pieces) {
    const socials = piece.placements.filter((placement) => placement.kind === 'social');
    posts += socials.length;
    const hasSocial = socials.length > 0;
    if (hasSocial) {
      adLicensesIncluded += 1;
    }
    const content = hasSocial
      ? input.rates.social
      : input.adLicenseSelected
        ? input.rates.advertising
        : input.rates.ugc;
    net += (content + durationRate(piece, input.rates)) * rank;
    for (const placement of socials) {
      if (placement.kind !== 'social') {
        continue;
      }
      net += input.audienceFees[placement.channel] ?? 0;
    }
  }

  return {
    uniquePieces: input.pieces.length,
    posts,
    net,
    adLicensesIncluded,
    adLicenseOptional: input.pieces.length > 0 && adLicensesIncluded === 0,
  };
}

/**
 * Pricing-model amounts are pence. The quote is in major units.
 *
 * Create-flow estimates only. A hire is never priced here: the drawer, the Review
 * application modal and the hire all use the API's quote (`PieceHireQuoteStore`).
 */
export function ratesFromPricingModel(model: {
  ugcPrice?: number | null;
  advertisingPrice?: number | null;
  socialPostPrice?: number | null;
  fifteenSecPrice?: number | null;
  thirtySecPrice?: number | null;
  sixtySecPrice?: number | null;
} | null | undefined): PiecePriceRates {
  const major = (pence: number | null | undefined) => (pence ?? 0) / 100;
  return {
    ugc: major(model?.ugcPrice),
    advertising: major(model?.advertisingPrice),
    social: major(model?.socialPostPrice),
    duration15: major(model?.fifteenSecPrice),
    duration30: major(model?.thirtySecPrice),
    duration60: major(model?.sixtySecPrice),
  };
}

/** Conservative rank for a quote before a creator is hired. Missing ranks stay at 1. */
export function estimateRankMultiplier(ranks: { multiplier?: number | null }[] | null | undefined): number {
  if (!ranks?.length) {
    return 1;
  }
  const multipliers = ranks.map((rank) => (rank.multiplier && rank.multiplier > 0 ? rank.multiplier : 1));
  return Math.min(...multipliers);
}

/** One audience-size fee, applied to every social placement on the quote. */
export function audienceFeesAt(pieces: Piece[], bonus: number): AudienceFees {
  const fees: AudienceFees = {};
  for (const piece of pieces) {
    for (const placement of piece.placements) {
      if (placement.kind === 'social') {
        fees[placement.channel] = bonus;
      }
    }
  }
  return fees;
}

function durationRate(piece: Piece, rates: PiecePriceRates): number {
  if (piece.durationSeconds === 15) {
    return rates.duration15;
  }
  if (piece.durationSeconds === 60) {
    return rates.duration60;
  }
  return rates.duration30;
}
